import type { MultilingualQuestion } from '../../types/multilingualQuestion';

// Multilingual version of Traffic Lights & Signals questions — CBR 2026 format
export const trafficLightsSignalsMultilingual: MultilingualQuestion[] = [
  {
    id: 'q-tls-1',
    animationId: null,
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/traffic-lights/q-tls-1.jpg',
    text: {
      en: 'You are driving at 50 km/h. The traffic light turns amber when you are 30 metres from the stop line. What do you do?',
      nl: 'U rijdt 50 km/u. Het verkeerslicht springt op oranje terwijl u nog 30 meter van de stopstreep verwijderd bent. Wat doet u?',
      ar: 'أنت تسير بسرعة 50 كم/س. تتحول إشارة المرور إلى الكهرماني وأنت على بعد 30 مترا من خط التوقف. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls-1o1',
        text: {
          en: 'Accelerate to clear the junction before it turns red.',
          nl: 'Optrekken om de kruising te passeren voor rood.',
          ar: 'تسارع لتجتاز التقاطع قبل أن يتحول إلى الأحمر.',
        },
      },
      {
        id: 'q-tls-1o2',
        text: {
          en: 'Brake and stop before the stop line.',
          nl: 'Remmen en stoppen voor de stopstreep.',
          ar: 'اكبح وتوقف قبل خط التوقف.',
        },
      },
      {
        id: 'q-tls-1o3',
        text: {
          en: 'Continue at the same speed — amber means green is coming.',
          nl: 'Doorrijden op dezelfde snelheid — oranje betekent dat groen komt.',
          ar: 'تابع بنفس السرعة — الكهرماني يعني الأخضر قادم.',
        },
      },
    ],
    correctAnswerId: 'q-tls-1o2',
    explanation: {
      en: 'At 30 metres and 50 km/h you can brake safely. Amber means stop unless stopping would cause danger. You have enough distance — stop.',
      nl: 'Op 30 meter en 50 km/u kunt u veilig remmen. Oranje betekent stoppen, tenzij stoppen gevaar oplevert. U heeft voldoende afstand — stop.',
      ar: 'على بعد 30 مترا وسرعة 50 كم/س يمكنك الفرملة بأمان. الكهرماني يعني التوقف إلا إذا كان التوقف سيسبب خطرا. لديك مسافة كافية — توقف.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-2',
    animationId: null,
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/traffic-lights/q-tls-2.jpg',
    text: {
      en: 'You are at a junction. The main traffic light is red, but a green arrow points left. Cyclists on the cycle path are crossing from the right. What do you do?',
      nl: 'U staat op een kruising. Het hoofdlicht is rood, maar een groene pijl wijst naar links. Fietsers op het fietspad steken over van rechts. Wat doet u?',
      ar: 'أنت عند تقاطع. الضوء الرئيسي أحمر، لكن سهما أخضر يشير إلى اليسار. دراجون على مسار الدراجات يعبرون من اليمين. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls-2o1',
        text: {
          en: 'Turn left, giving way to the cyclists.',
          nl: 'Linksaf slaan en de fietsers voorrang verlenen.',
          ar: 'انعطف يسارا مع إعطاء الأولوية للدراجين.',
        },
      },
      {
        id: 'q-tls-2o2',
        text: {
          en: 'Wait for the main green light before turning.',
          nl: 'Wachten op het groene hoofdlicht voor het afslaan.',
          ar: 'انتظر الضوء الأخضر الرئيسي قبل الانعطاف.',
        },
      },
      {
        id: 'q-tls-2o3',
        text: {
          en: 'Turn left — the green arrow gives priority over cyclists.',
          nl: 'Linksaf slaan — de groene pijl geeft voorrang op fietsers.',
          ar: 'انعطف يسارا — السهم الأخضر يمنحك الأولوية على الدراجين.',
        },
      },
    ],
    correctAnswerId: 'q-tls-2o1',
    explanation: {
      en: 'A green filter arrow lets you proceed in that direction despite red. You must still give way to cyclists and pedestrians crossing your path.',
      nl: 'Een groene filterpijl staat u toe die richting op te rijden ondanks rood. U moet fietsers en voetgangers die uw pad kruisen altijd voorrang geven.',
      ar: 'يسمح لك السهم الأخضر المرشح بالسير في ذلك الاتجاه رغم الأحمر. يجب عليك منح الأولوية للدراجين والمشاة الذين يقطعون مسارك.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-3',
    animationId: null,
    text: {
      en: 'You approach a junction where all traffic lights are dark. No police are present. A car is approaching from your right on an equal road. What do you do?',
      nl: 'U nadert een kruising waar alle verkeerslichten donker zijn. Er is geen politie. Een auto nadert van rechts op een gelijkwaardige weg. Wat doet u?',
      ar: 'تقترب من تقاطع حيث جميع إشارات المرور مطفأة. لا يوجد شرطة. سيارة تقترب من يمينك على طريق مماثل. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls-3o1',
        text: {
          en: 'Proceed — you are on the main road.',
          nl: 'Doorrijden — u rijdt op de hoofdweg.',
          ar: 'تابع — أنت على الطريق الرئيسي.',
        },
      },
      {
        id: 'q-tls-3o2',
        text: {
          en: 'Give way to the car from the right; apply normal right-of-way rules.',
          nl: 'Voorrang verlenen aan de auto van rechts; normale voorrangsregels toepassen.',
          ar: 'أعط الأولوية للسيارة القادمة من اليمين؛ طبق قواعد الأولوية العادية.',
        },
      },
      {
        id: 'q-tls-3o3',
        text: {
          en: 'Stop and wait for a police officer before entering.',
          nl: 'Stoppen en wachten op een politieagent voor u de kruising oprijdt.',
          ar: 'توقف وانتظر ضابط شرطة قبل دخول التقاطع.',
        },
      },
    ],
    correctAnswerId: 'q-tls-3o2',
    explanation: {
      en: 'Dark traffic lights mean revert to default rules. On equal roads, give way to the right (rechts heeft voorrang) unless signs say otherwise.',
      nl: 'Donkere verkeerslichten betekenen terugvallen op de standaardregels. Op gelijkwaardige wegen: rechts heeft voorrang, tenzij borden anders aangeven.',
      ar: 'الإشارات المطفأة تعني الرجوع إلى القواعد الاعتيادية. على الطرق المتكافئة: الأولوية للقادم من اليمين، إلا إذا نصت العلامات على خلاف ذلك.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-4',
    animationId: null,
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/traffic-lights/q-tls-4.jpg',
    text: {
      en: 'Your traffic light is green. An elderly pedestrian is already walking slowly across the crossing ahead. What does the green light mean in this situation?',
      nl: 'Uw verkeerslicht is groen. Een oudere voetganger loopt al langzaam over de oversteek voor u. Wat betekent het groene licht in deze situatie?',
      ar: 'إشارتك خضراء. أحد كبار السن يسير ببطء بالفعل عبر الممر أمامك. ماذا يعني الضوء الأخضر في هذه الحالة؟',
    },
    options: [
      {
        id: 'q-tls-4o1',
        text: {
          en: 'You have full priority — proceed without stopping.',
          nl: 'U heeft volledige voorrang — doorrijden zonder te stoppen.',
          ar: 'لديك الأولوية الكاملة — تابع دون توقف.',
        },
      },
      {
        id: 'q-tls-4o2',
        text: {
          en: 'You may proceed only when the crossing is completely clear.',
          nl: 'U mag pas doorrijden als de oversteek volledig vrij is.',
          ar: 'يمكنك المتابعة فقط عندما يكون الممر خاليا تماما.',
        },
      },
      {
        id: 'q-tls-4o3',
        text: {
          en: 'Sound your horn to ask the pedestrian to hurry.',
          nl: 'Claxonneren om de voetganger te vragen op te schieten.',
          ar: 'استخدم البوق لطلب من المشاة الإسراع.',
        },
      },
    ],
    correctAnswerId: 'q-tls-4o2',
    explanation: {
      en: 'A green light means you may proceed — it does not give you priority over pedestrians already on the crossing. You must wait until the crossing is completely clear before moving forward.',
      nl: 'Groen licht betekent dat u mag rijden — het geeft u geen voorrang op voetgangers die al op de oversteek zijn. U moet wachten tot de oversteek volledig vrij is.',
      ar: 'الضوء الأخضر يعني أنك مسموح لك بالمرور — لكنه لا يمنحك الأولوية على المشاة الموجودين بالفعل على الممر. يجب أن تنتظر حتى يخلو الممر تماما.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-5',
    animationId: null,
    text: {
      en: 'A police officer is directing traffic at a junction and signals you to stop, but the traffic light for you is green. What must you do?',
      nl: 'Een politieagent regelt het verkeer bij een kruising en geeft u het signaal om te stoppen, maar het verkeerslicht voor u is groen. Wat moet u doen?',
      ar: 'ضابط شرطة يوجه حركة المرور عند تقاطع ويشير لك بالتوقف، لكن إشارة المرور لك خضراء. ماذا يجب عليك أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls-5o1',
        text: {
          en: 'Obey the green traffic light and proceed.',
          nl: 'Het groene verkeerslicht gehoorzamen en doorrijden.',
          ar: 'امتثل لإشارة المرور الخضراء وتابع.',
        },
      },
      {
        id: 'q-tls-5o2',
        text: {
          en: 'Point to the green light to inform the officer and then proceed.',
          nl: 'Naar het groene licht wijzen om de agent te informeren en dan doorrijden.',
          ar: 'أشر إلى الضوء الأخضر لإبلاغ الضابط ثم تابع.',
        },
      },
      {
        id: 'q-tls-5o3',
        text: {
          en: "Obey the police officer's signal and stop.",
          nl: 'Het signaal van de politieagent gehoorzamen en stoppen.',
          ar: 'امتثل لإشارة ضابط الشرطة وتوقف.',
        },
      },
    ],
    correctAnswerId: 'q-tls-5o3',
    explanation: {
      en: 'Signals given by a police officer directing traffic override all other traffic signals and signs. You must obey the officer.',
      nl: 'Signalen van een politieagent die het verkeer regelt hebben voorrang op alle andere signalen en borden. U moet de agent gehoorzamen.',
      ar: 'الإشارات التي يعطيها ضابط شرطة يوجه المرور تتجاوز جميع إشارات ولافتات المرور الأخرى. يجب عليك الامتثال للضابط.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-6',
    animationId: null,
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/traffic-lights/q-tls-6.jpg',
    text: {
      en: 'At a level crossing, twin flashing red lights have started to flash. What is your immediate action?',
      nl: 'Bij een spoorwegovergang zijn de dubbele rode knipperlichten begonnen te knipperen. Wat is uw onmiddellijke actie?',
      ar: 'عند معبر السكك الحديدية، بدأت أضواء حمراء مزدوجة وامضة في الوميض. ما هي إجراءاتك الفورية؟',
    },
    options: [
      {
        id: 'q-tls-6o1',
        text: {
          en: 'Stop immediately, even if you are already on the crossing.',
          nl: 'Onmiddellijk stoppen, zelfs als u al op de overgang bent.',
          ar: 'توقف فورا، حتى لو كنت بالفعل على المعبر.',
        },
      },
      {
        id: 'q-tls-6o2',
        text: {
          en: 'Stop before the barrier or stop line and wait until the lights stop flashing and any barrier is fully raised.',
          nl: 'Stoppen voor de slagboom of stopstreep en wachten tot de lichten stoppen en de slagboom volledig omhoog is.',
          ar: 'توقف قبل الحاجز أو خط التوقف وانتظر حتى تتوقف الأضواء عن الوميض وأي حاجز يتم رفعه بالكامل.',
        },
      },
      {
        id: 'q-tls-6o3',
        text: {
          en: 'Cross quickly if you cannot see or hear a train approaching.',
          nl: 'Snel oversteken als u geen trein ziet of hoort naderen.',
          ar: 'اعبر بسرعة إذا لم تتمكن من رؤية أو سماع قطار قادم.',
        },
      },
    ],
    correctAnswerId: 'q-tls-6o2',
    explanation: {
      en: 'Twin flashing red lights at a level crossing mean a train is approaching. Stop before the stop line or barrier and wait. If you are already on the crossing when the lights activate, clear it immediately — never stop on the tracks.',
      nl: 'Dubbele knipperende rode lichten bij een spoorwegovergang betekenen dat een trein nadert. Stop voor de stopstreep of slagboom en wacht. Als u al op de overgang staat wanneer de lichten gaan, rijd er dan direct af — stop nooit op het spoor.',
      ar: 'الأضواء الحمراء المزدوجة الوامضة عند معبر السكك الحديدية تعني أن قطارا يقترب. توقف قبل خط التوقف أو الحاجز وانتظر. إذا كنت بالفعل على المعبر عند تشغيل الأضواء، اخرج منه فورا — لا تتوقف أبدا على القضبان.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-7',
    animationId: null,
    text: {
      en: "A tram ahead of you at a junction shows a steady white horizontal bar on its signal and starts braking. A gap opens to the tram's right. What do you do?",
      nl: 'Een tram voor u bij een kruising toont een vaste witte horizontale balk en begint te remmen. Rechts van de tram ontstaat een opening. Wat doet u?',
      ar: 'ترام أمامك عند تقاطع يظهر شريطا أبيضا أفقيا ثابتا في إشارته ويبدأ بالفرملة. تظهر فجوة على يمين الترام. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls-7o1',
        text: {
          en: 'Pass the tram through the gap on the right.',
          nl: 'De tram rechts inhalen via de opening.',
          ar: 'تجاوز الترام من اليمين عبر الفجوة.',
        },
      },
      {
        id: 'q-tls-7o2',
        text: {
          en: 'Sound your horn — the tram is blocking traffic without reason.',
          nl: 'Claxonneren — de tram blokkeert het verkeer zonder reden.',
          ar: 'استخدم البوق — الترام يعيق المرور دون سبب.',
        },
      },
      {
        id: 'q-tls-7o3',
        text: {
          en: 'Stay behind the tram — it must stop and the gap may close suddenly.',
          nl: 'Achter de tram blijven — hij moet stoppen en de opening kan plotseling sluiten.',
          ar: 'ابق خلف الترام — يجب أن يتوقف والفجوة قد تغلق فجأة.',
        },
      },
    ],
    correctAnswerId: 'q-tls-7o3',
    explanation: {
      en: 'A white horizontal bar means the tram must stop. Never pass a braking tram — cyclists or pedestrians may be hidden in that gap.',
      nl: 'Een witte horizontale balk betekent dat de tram moet stoppen. Haal een remmende tram nooit in — er kunnen fietsers of voetgangers in die opening schuilen.',
      ar: 'الشريط الأبيض الأفقي يعني أن الترام يجب أن يتوقف. لا تتجاوز تراما يكبح أبدا — قد يكون هناك دراجون أو مشاة مختبئون في تلك الفجوة.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-8',
    animationId: null,
    text: {
      en: 'Your traffic light turns green. Next to it a red cycle signal is still lit. You want to turn right. What must you do?',
      nl: 'Uw verkeerslicht wordt groen. Ernaast staat nog een rood fietslicht. U wilt rechtsaf. Wat moet u doen?',
      ar: 'إشارتك تتحول إلى الأخضر. بجانبها إشارة دراجات حمراء لا تزال مضاءة. تريد الانعطاف يمينا. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls-8o1',
        text: {
          en: 'Turn right freely — cyclists have red and must stop.',
          nl: 'Vrij rechtsaf slaan — fietsers hebben rood en moeten stoppen.',
          ar: 'انعطف يمينا بحرية — الدراجون لديهم أحمر ويجب أن يتوقفوا.',
        },
      },
      {
        id: 'q-tls-8o2',
        text: {
          en: 'Turn right carefully, checking for cyclists who may not have stopped.',
          nl: 'Voorzichtig rechtsaf, controleren op fietsers die mogelijk niet gestopt zijn.',
          ar: 'انعطف يمينا بحذر، تحقق من الدراجين الذين قد لا يكونوا توقفوا.',
        },
      },
      {
        id: 'q-tls-8o3',
        text: {
          en: 'Wait for the cycle signal to change before turning.',
          nl: 'Wachten tot het fietslicht verandert voor het afslaan.',
          ar: 'انتظر حتى تتغير إشارة الدراجات قبل الانعطاف.',
        },
      },
    ],
    correctAnswerId: 'q-tls-8o2',
    explanation: {
      en: 'Cyclists must obey red, but you must still check before turning. Never assume all cyclists have stopped just because their signal is red.',
      nl: 'Fietsers moeten rood gehoorzamen, maar u moet toch controleren voor het afslaan. Ga er nooit van uit dat alle fietsers gestopt zijn omdat hun licht rood is.',
      ar: 'يجب على الدراجين الامتثال للأحمر، لكن يجب عليك التحقق قبل الانعطاف. لا تفترض أبدا أن جميع الدراجين توقفوا لمجرد أن إشارتهم حمراء.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-9',
    animationId: null,
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/traffic-lights/q-tls-9.jpg',
    text: {
      en: 'A bus ahead is pulling out from a bus stop into your lane. What must you do?',
      nl: 'Een bus voor u rijdt de bushalte uit naar uw rijstrook. Wat moet u doen?',
      ar: 'حافلة أمامك تتحرك من محطة الحافلات إلى مسارك. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls-9o1',
        text: {
          en: 'Maintain your speed — you are in the lane and have priority.',
          nl: 'Uw snelheid handhaven — u rijdt op de rijstrook en heeft voorrang.',
          ar: 'حافظ على سرعتك — أنت في المسار ولك الأولوية.',
        },
      },
      {
        id: 'q-tls-9o2',
        text: {
          en: 'Give way to the bus — it has priority when leaving a bus stop.',
          nl: 'Voorrang geven aan de bus — hij heeft voorrang bij het verlaten van een bushalte.',
          ar: 'أعط الأولوية للحافلة — لها الأولوية عند مغادرة المحطة.',
        },
      },
      {
        id: 'q-tls-9o3',
        text: {
          en: 'Sound your horn to warn the bus driver.',
          nl: 'Claxonneren om de buschauffeur te waarschuwen.',
          ar: 'استخدم البوق لتحذير سائق الحافلة.',
        },
      },
    ],
    correctAnswerId: 'q-tls-9o2',
    explanation: {
      en: 'Under RVV 1990 article 50, within a built-up area you must give way to buses leaving a marked bus stop. Slow down and let it merge safely.',
      nl: 'Artikel 50 RVV verplicht u binnen de bebouwde kom voorrang te geven aan bussen die een bushalte verlaten. Vertraag en laat de bus veilig invoegen.',
      ar: 'بموجب المادة 50 من لائحة المرور RVV 1990، يجب إعطاء الأولوية للحافلات التي تغادر المحطة داخل المناطق المبنية. أبطئ ودعها تندمج بأمان.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-10',
    animationId: null,
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/traffic-lights/q-tls-10.jpg',
    text: {
      en: 'You are stopped at a red light. The signal now shows red AND amber lit together. What does this mean and what should you do?',
      nl: 'U staat stil voor rood. De lichten tonen nu rood ÉN oranje tegelijk. Wat betekent dit en wat doet u?',
      ar: 'أنت واقف عند الإشارة الحمراء. تظهر الإشارة الآن الأحمر والكهرماني معا. ماذا يعني ذلك وماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls-10o1',
        text: {
          en: 'Move off immediately — it means go.',
          nl: 'Meteen optrekken — het betekent rijden.',
          ar: 'انطلق فورا — يعني السير.',
        },
      },
      {
        id: 'q-tls-10o2',
        text: {
          en: 'Stay still — red and amber together signals a fault.',
          nl: 'Stilstaan — rood en oranje samen duidt op een storing.',
          ar: 'ابق ثابتا — الأحمر والكهرماني معا يشير إلى عطل.',
        },
      },
      {
        id: 'q-tls-10o3',
        text: {
          en: 'Get ready to move but wait for full green before going.',
          nl: 'Voorbereiden op vertrek maar wachten op volledig groen.',
          ar: 'استعد للانطلاق لكن انتظر الأخضر الكامل قبل الحركة.',
        },
      },
    ],
    correctAnswerId: 'q-tls-10o3',
    explanation: {
      en: 'Red and amber together means "prepare to go." You must still wait for full green before moving. Do not move on red-amber — it is not yet permission to go.',
      nl: 'Rood en oranje samen betekent "voorbereiden op vertrek." U moet wachten op volledig groen voordat u rijdt. Rij niet bij rood-oranje — het is nog geen toestemming.',
      ar: 'الأحمر والكهرماني معا يعني «الاستعداد للانطلاق». يجب انتظار الضوء الأخضر الكامل قبل التحرك. لا تتحرك عند الأحمر-الكهرماني — فهو ليس إذنا بعد.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-new-1',
    animationId: null,
    text: {
      en: 'You are driving at 50 km/h. The traffic light turns amber when you are only 10 metres from the stop line. Stopping sharply now would risk a rear-end collision. What do you do?',
      nl: 'U rijdt 50 km/u. Het verkeerslicht springt op oranje terwijl u nog slechts 10 meter van de stopstreep bent. Hard remmen nu zou een kop-staartbotsing riskeren. Wat doet u?',
      ar: 'أنت تسير بسرعة 50 كم/س. تتحول إشارة المرور إلى الكهرماني وأنت على بعد 10 أمتار فقط من خط التوقف. الفرملة الحادة الآن ستخاطر بتصادم خلفي. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls-new-1-o1',
        text: {
          en: 'Brake hard and stop before the stop line regardless.',
          nl: 'Hard remmen en voor de stopstreep stoppen, ongeacht de situatie.',
          ar: 'اكبح بشدة وتوقف قبل خط التوقف بغض النظر.',
        },
      },
      {
        id: 'q-tls-new-1-o2',
        text: {
          en: 'Continue through carefully at normal speed — stopping is not safe.',
          nl: 'Voorzichtig op normale snelheid doorrijden — stoppen is niet veilig.',
          ar: 'تابع بحذر بسرعة طبيعية — التوقف غير آمن.',
        },
      },
      {
        id: 'q-tls-new-1-o3',
        text: {
          en: 'Accelerate hard to clear the junction as quickly as possible.',
          nl: 'Hard optrekken om de kruising zo snel mogelijk te passeren.',
          ar: 'تسارع بشدة لتجتاز التقاطع بأسرع ما يمكن.',
        },
      },
    ],
    correctAnswerId: 'q-tls-new-1-o2',
    explanation: {
      en: 'Amber means stop — unless stopping would cause danger. At 10 metres and 50 km/h you cannot stop safely. Continue at normal speed — do not accelerate.',
      nl: 'Oranje betekent stoppen — tenzij stoppen gevaar oplevert. Op 10 meter en 50 km/u kunt u niet veilig stoppen. Rijd op normale snelheid door — niet optrekken.',
      ar: 'الكهرماني يعني التوقف — إلا إذا كان التوقف سيسبب خطرا. على بعد 10 أمتار وسرعة 50 كم/س لا يمكنك التوقف بأمان. تابع بسرعة طبيعية — لا تتسارع.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-new-2',
    animationId: null,
    text: {
      en: 'The red lights at a level crossing are flashing and the barriers have just started to lower. Your car is a few metres from the barrier. What do you do?',
      nl: 'De rode lichten bij een spoorwegovergang knipperen en de slagbomen beginnen net te zakken. Uw auto staat op enkele meters van de slagboom. Wat doet u?',
      ar: 'الأضواء الحمراء عند معبر السكة الحديدية تومض والحواجز بدأت للتو في الانخفاض. سيارتك على بعد أمتار قليلة من الحاجز. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls-new-2-o1',
        text: {
          en: 'Drive quickly under the barrier before it fully closes.',
          nl: 'Snel onder de slagboom doorrijden voor hij volledig sluit.',
          ar: 'اقتحم تحت الحاجز بسرعة قبل أن يغلق تماما.',
        },
      },
      {
        id: 'q-tls-new-2-o2',
        text: {
          en: 'Stop immediately and wait until barriers are fully raised and lights stop.',
          nl: 'Direct stoppen en wachten tot de slagbomen volledig omhoog zijn en de lichten stoppen.',
          ar: 'توقف فورا وانتظر حتى يرفع الحاجزان بالكامل وتتوقف الأضواء.',
        },
      },
      {
        id: 'q-tls-new-2-o3',
        text: {
          en: 'Reverse away from the barrier to create distance.',
          nl: 'Achteruit rijden om afstand te creëren van de slagboom.',
          ar: 'تراجع بعيدا عن الحاجز لإبعاد المسافة.',
        },
      },
    ],
    correctAnswerId: 'q-tls-new-2-o2',
    explanation: {
      en: 'Flashing red means a train is approaching — stop immediately. Never pass under a lowering barrier. Wait until both barriers are fully raised and lights stop flashing.',
      nl: 'Knipperend rood betekent dat een trein nadert — direct stoppen. Rijd nooit onder een dalende slagboom. Wacht tot beide slagbomen volledig omhoog zijn en de lichten stoppen.',
      ar: 'الأحمر الوامض يعني اقتراب قطار — توقف فورا. لا تمر أبدا تحت حاجز ينزل. انتظر حتى يرفع كلا الحاجزين بالكامل وتتوقف الأضواء عن الوميض.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-1',
    animationId: null,
    text: {
      en: 'The traffic light has been green for a long time. You are approaching at 50 km/h. What should you keep in mind?',
      nl: 'Het verkeerslicht is al lang groen. U nadert met 50 km/u. Waarmee moet u rekening houden?',
      ar: 'أصبحت إشارة المرور خضراء منذ وقت طويل. أنت تقترب بسرعة 50 كم/س. ماذا يجب أن تضع في اعتبارك؟',
    },
    options: [
      {
        id: 'q-tls2-1o1',
        text: {
          en: 'Speed up to pass before it changes',
          nl: 'Versnellen om erdoorheen te rijden voordat het verandert',
          ar: 'زد السرعة لتمر قبل أن تتغير',
        },
      },
      {
        id: 'q-tls2-1o2',
        text: {
          en: 'Be prepared for it to change to amber; be ready to stop safely',
          nl: 'Wees erop voorbereid dat het oranje wordt; wees klaar om veilig te stoppen',
          ar: 'كن مستعدا لأن تتحول إلى الكهرماني؛ كن مستعدا للتوقف بأمان',
        },
      },
      {
        id: 'q-tls2-1o3',
        text: {
          en: 'Green means go; no need to slow down',
          nl: 'Groen betekent doorrijden; geen reden om te vertragen',
          ar: 'الأخضر يعني المضي؛ لا حاجة للإبطاء',
        },
      },
    ],
    correctAnswerId: 'q-tls2-1o2',
    explanation: {
      en: 'A light that has been green for a long time may change soon. Be prepared to stop safely if it turns amber.',
      nl: 'Een licht dat al lang groen is, kan binnenkort veranderen. Wees voorbereid om veilig te stoppen als het oranje wordt.',
      ar: 'قد يتغير الضوء الأخضر الذي بقي طويلا قريبا. كن مستعدا للتوقف بأمان إذا تحول إلى الكهرماني.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-2',
    animationId: null,
    text: {
      en: 'You are at a junction. The traffic light is red. A green arrow for turning left is also lit. What may you do?',
      nl: 'U bevindt zich op een kruising. Het verkeerslicht is rood. Er brandt ook een groene pijl voor linksaf. Wat mag u doen?',
      ar: 'أنت عند تقاطع. إشارة المرور حمراء. يضيء أيضا سهم أخضر للانعطاف يسارا. ماذا يجوز لك أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-2o1',
        text: {
          en: 'You may turn left, giving way to cyclists and pedestrians crossing your path',
          nl: 'U mag linksaf, voorrang verlenend aan fietsers en voetgangers die uw pad kruisen',
          ar: 'يجوز لك الانعطاف يسارا مع إعطاء الأولوية للدراجين والمشاة الذين يعبرون مسارك',
        },
      },
      {
        id: 'q-tls2-2o2',
        text: {
          en: 'You must wait for the full green light',
          nl: 'U moet wachten op het volledige groene licht',
          ar: 'يجب عليك انتظار الضوء الأخضر الكامل',
        },
      },
      {
        id: 'q-tls2-2o3',
        text: {
          en: 'You may turn left with full priority over all other road users',
          nl: 'U mag linksaf met volledige voorrang op alle andere weggebruikers',
          ar: 'يجوز لك الانعطاف يسارا بأولوية كاملة على جميع مستخدمي الطريق',
        },
      },
    ],
    correctAnswerId: 'q-tls2-2o1',
    explanation: {
      en: 'A green filter arrow allows you to proceed in that direction despite the red main light. You must still give way to cyclists and pedestrians crossing your path.',
      nl: 'Een groene filterpijl staat u toe in die richting te rijden ondanks het rode hoofdlicht. U moet fietsers en voetgangers die uw pad kruisen altijd voorrang geven.',
      ar: 'السهم الأخضر المرشح يسمح لك بالمضي في ذلك الاتجاه رغم الضوء الأحمر الرئيسي. يجب أن تمنح الأولوية للدراجين والمشاة الذين يعبرون مسارك.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-3',
    animationId: null,
    text: {
      en: 'At a pedestrian crossing with traffic lights, the light is green for you but a pedestrian has just stepped onto the crossing. What must you do?',
      nl: 'Bij een voetgangersoversteekplaats met verkeerslichten is het licht voor u groen, maar een voetganger is net de oversteek op gegaan. Wat moet u doen?',
      ar: 'عند معبر مشاة مع إشارات مرور، ضوءك أخضر لكن أحد المشاة خطا للتو على المعبر. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-3o1',
        text: {
          en: 'Proceed; the light is green',
          nl: 'Doorrijden; het licht is groen',
          ar: 'تابع؛ الضوء أخضر',
        },
      },
      {
        id: 'q-tls2-3o2',
        text: {
          en: 'Give way to the pedestrian; they are on the crossing',
          nl: 'Voorrang verlenen aan de voetganger; die staat op de oversteek',
          ar: 'أعط الأولوية للمشاة؛ هم على المعبر',
        },
      },
      {
        id: 'q-tls2-3o3',
        text: {
          en: 'Sound your horn to warn the pedestrian',
          nl: 'Claxonneren om de voetganger te waarschuwen',
          ar: 'استخدم البوق لتحذير المشاة',
        },
      },
    ],
    correctAnswerId: 'q-tls2-3o2',
    explanation: {
      en: 'You must give way to pedestrians who are already on the crossing, even if your light is green.',
      nl: 'U moet voorrang verlenen aan voetgangers die al op de oversteek zijn, ook als uw licht groen is.',
      ar: 'يجب أن تمنح الأولوية للمشاة الذين هم بالفعل على المعبر، حتى لو كان ضوءك أخضر.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-4',
    animationId: null,
    text: {
      en: 'You approach a junction. The main light is red but a green arrow points left. A pedestrian is crossing from the left side of your path. What do you do?',
      nl: 'U nadert een kruising. Het hoofdlicht is rood maar een groene pijl wijst naar links. Een voetganger steekt over van links voor u. Wat doet u?',
      ar: 'تقترب من تقاطع. الضوء الرئيسي أحمر لكن سهما أخضر يشير يسارا. أحد المشاة يعبر من اليسار أمامك. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-4o1',
        text: {
          en: 'Stop — the main light is red',
          nl: 'Stoppen — het hoofdlicht is rood',
          ar: 'توقف — الضوء الرئيسي أحمر',
        },
      },
      {
        id: 'q-tls2-4o2',
        text: {
          en: 'Turn left, giving way to the pedestrian crossing',
          nl: 'Linksaf slaan en de overstekende voetganger voorrang verlenen',
          ar: 'انعطف يسارا مع إعطاء الأولوية للمشاة الذين يعبرون',
        },
      },
      {
        id: 'q-tls2-4o3',
        text: {
          en: 'Turn left — the green arrow gives priority over pedestrians',
          nl: 'Linksaf slaan — de groene pijl geeft voorrang op voetgangers',
          ar: 'انعطف يسارا — السهم الأخضر يمنحك الأولوية على المشاة',
        },
      },
    ],
    correctAnswerId: 'q-tls2-4o2',
    explanation: {
      en: 'A green filter arrow allows you to turn in its direction despite the red main light. You must still give way to pedestrians and cyclists crossing your path.',
      nl: 'Een groene filterpijl staat u toe die richting op te rijden ondanks het rode hoofdlicht. U moet voetgangers en fietsers die uw pad kruisen altijd voorrang geven.',
      ar: 'يسمح لك السهم الأخضر المرشح بالانعطاف في اتجاهه رغم الضوء الأحمر الرئيسي. يجب عليك دائما منح الأولوية للمشاة والدراجين الذين يعبرون مسارك.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-5',
    animationId: null,
    text: {
      en: 'Twin red lights are flashing at a level crossing. No train is visible in either direction. What must you do?',
      nl: 'Dubbele rode lichten knipperen bij een spoorwegovergang. Er is geen trein zichtbaar in beide richtingen. Wat moet u doen?',
      ar: 'أضواء حمراء مزدوجة تومض عند معبر سكة حديد. لا يوجد قطار مرئي في أي اتجاه. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-5o1',
        text: {
          en: 'Cross quickly — no train is coming',
          nl: 'Snel oversteken — er komt geen trein',
          ar: 'اعبر بسرعة — لا يوجد قطار قادم',
        },
      },
      {
        id: 'q-tls2-5o2',
        text: {
          en: 'Stop and wait — flashing red means a train is approaching regardless of visibility',
          nl: 'Stoppen en wachten — knipperend rood betekent dat een trein nadert ongeacht de zichtbaarheid',
          ar: 'توقف وانتظر — الأحمر الوامض يعني اقتراب قطار بغض النظر عن الرؤية',
        },
      },
      {
        id: 'q-tls2-5o3',
        text: {
          en: 'Slow down and cross carefully',
          nl: 'Vertragen en voorzichtig oversteken',
          ar: 'أبطئ واعبر بحذر',
        },
      },
    ],
    correctAnswerId: 'q-tls2-5o2',
    explanation: {
      en: 'Flashing red at a level crossing always means stop — even if no train is visible. Trains can approach fast from out of sight. Wait until both lights stop and any barriers are fully raised.',
      nl: 'Knipperend rood bij een spoorwegovergang betekent altijd stoppen — ook als er geen trein zichtbaar is. Treinen kunnen snel naderen vanuit het zicht. Wacht tot de lichten stoppen en de slagbomen volledig omhoog zijn.',
      ar: 'الأحمر الوامض عند معبر السكة يعني دائما التوقف — حتى لو لم يكن أي قطار مرئيا. القطارات يمكن أن تقترب بسرعة من خارج نطاق الرؤية. انتظر حتى تتوقف الأضواء وترتفع الحواجز بالكامل.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-6',
    animationId: null,
    text: {
      en: 'You are driving behind a tram at a junction. Its signal shows a steady white horizontal bar. The tram slows. A gap opens to its right. What do you do?',
      nl: 'U rijdt achter een tram bij een kruising. Zijn sein toont een vaste witte horizontale balk. De tram vertraagt. Rechts ontstaat een opening. Wat doet u?',
      ar: 'أنت تسير خلف ترام عند تقاطع. إشارته تظهر شريطا أبيضا أفقيا ثابتا. الترام يبطئ. تظهر فجوة على يمينه. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-6o1',
        text: {
          en: 'Pass the tram through the gap on the right',
          nl: 'De tram via de opening rechts inhalen',
          ar: 'تجاوز الترام عبر الفجوة من اليمين',
        },
      },
      {
        id: 'q-tls2-6o2',
        text: {
          en: 'Stay behind the tram — it must stop, the gap may close suddenly',
          nl: 'Achter de tram blijven — hij moet stoppen en de opening kan plotseling sluiten',
          ar: 'ابق خلف الترام — يجب أن يتوقف، والفجوة قد تغلق فجأة',
        },
      },
      {
        id: 'q-tls2-6o3',
        text: {
          en: 'The white bar means go — the tram is making way for you',
          nl: 'De witte balk betekent rijden — de tram maakt ruimte voor u',
          ar: 'الشريط الأبيض يعني المضي — الترام يفسح لك المجال',
        },
      },
    ],
    correctAnswerId: 'q-tls2-6o2',
    explanation: {
      en: 'A white horizontal bar means the tram must stop. Never pass a braking tram on the right — cyclists or pedestrians may be hidden in the gap.',
      nl: 'Een witte horizontale balk betekent dat de tram moet stoppen. Haal een remmende tram nooit rechts in — er kunnen fietsers of voetgangers in die opening schuilen.',
      ar: 'الشريط الأبيض الأفقي يعني أن الترام يجب أن يتوقف. لا تتجاوز تراما يكبح من اليمين أبدا — قد يختبئ دراجون أو مشاة في تلك الفجوة.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-7',
    animationId: null,
    text: {
      en: 'You are stopped at a red light. It changes to red and amber together. The driver behind you starts honking. What do you do?',
      nl: 'U wacht bij rood. Het licht springt op rood en oranje tegelijk. De bestuurder achter u begint te toeteren. Wat doet u?',
      ar: 'تنتظر عند الأحمر. يتحول الضوء إلى أحمر وكهرماني معا. السائق خلفك يبدأ بالتبويق. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-7o1',
        text: {
          en: 'Ignore the horn, stay ready, move only when fully green',
          nl: 'Het getoeter negeren, klaar blijven, alleen rijden bij volledig groen',
          ar: 'تجاهل البوق، ابق مستعدا، انطلق فقط عند الأخضر الكامل',
        },
      },
      {
        id: 'q-tls2-7o2',
        text: {
          en: 'Move off now — red and amber means go',
          nl: 'Nu optrekken — rood en oranje betekent rijden',
          ar: 'انطلق الآن — الأحمر والكهرماني يعني السير',
        },
      },
      {
        id: 'q-tls2-7o3',
        text: {
          en: 'Stay completely still — the lights may be faulty',
          nl: 'Volledig stilblijven — de lichten kunnen defect zijn',
          ar: 'ابق ثابتا تماما — الأضواء قد تكون معطلة',
        },
      },
    ],
    correctAnswerId: 'q-tls2-7o1',
    explanation: {
      en: 'Red and amber means prepare — not go. Wait for full green. An impatient driver behind has no right to rush you. Moving early is not permitted.',
      nl: 'Rood en oranje betekent voorbereiden — niet rijden. Wacht op volledig groen. Een ongeduldig bestuurder achter u heeft geen recht u te haasten. Vroeg rijden is niet toegestaan.',
      ar: 'الأحمر والكهرماني يعني الاستعداد — وليس السير. انتظر الأخضر الكامل. السائق غير الصبور خلفك لا يحق له استعجالك. الانطلاق المبكر غير مسموح.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-8',
    animationId: null,
    text: {
      en: 'You arrive at a junction where all traffic lights are dark. A car approaches from your right on an equal road. No signs are visible. What do you do?',
      nl: 'U komt aan bij een kruising waar alle verkeerslichten donker zijn. Een auto nadert van rechts op een gelijkwaardige weg. Er zijn geen borden. Wat doet u?',
      ar: 'تصل إلى تقاطع حيث جميع إشارات المرور مطفأة. سيارة تقترب من يمينك على طريق مماثل. لا توجد علامات. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-8o1',
        text: {
          en: 'Proceed — you arrived at the junction first',
          nl: 'Doorrijden — u was er als eerste',
          ar: 'تابع — لقد وصلت إلى التقاطع أولا',
        },
      },
      {
        id: 'q-tls2-8o2',
        text: {
          en: 'Give way to the car from the right — apply priority-to-the-right',
          nl: 'De auto van rechts voorrang verlenen — voorrang van rechts toepassen',
          ar: 'أعط الأولوية للسيارة من اليمين — طبق قاعدة أولوية اليمين',
        },
      },
      {
        id: 'q-tls2-8o3',
        text: {
          en: 'Stop and wait indefinitely; the junction is out of use',
          nl: 'Stoppen en onbepaald wachten; de kruising is buiten gebruik',
          ar: 'توقف وانتظر إلى أجل غير مسمى؛ التقاطع خارج الخدمة',
        },
      },
    ],
    correctAnswerId: 'q-tls2-8o2',
    explanation: {
      en: 'Dark lights mean revert to default rules. On equal roads: rechts heeft voorrang — give way to the right. Keep moving safely, do not wait indefinitely.',
      nl: 'Donkere lichten betekenen terugvallen op de standaardregels. Op gelijkwaardige wegen: rechts heeft voorrang. Rijd veilig door — wacht niet onbepaald.',
      ar: 'الإشارات المطفأة تعني الرجوع إلى القواعد الاعتيادية. على الطرق المتكافئة: الأولوية لليمين. تابع بأمان — لا تنتظر إلى أجل غير مسمى.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-9',
    animationId: null,
    text: {
      en: 'Your light turns green at a junction. A separate green cycle signal is also lit. You want to turn right. What must you check before turning?',
      nl: 'Uw licht wordt groen bij een kruising. Er brandt ook een groen fietssignaal. U wilt rechtsaf. Wat controleert u voor het afslaan?',
      ar: 'إشارتك تتحول إلى الأخضر عند تقاطع. إشارة دراجات خضراء منفصلة مضاءة أيضا. تريد الانعطاف يمينا. ماذا يجب أن تتحقق قبل الانعطاف؟',
    },
    options: [
      {
        id: 'q-tls2-9o1',
        text: {
          en: 'Nothing — your green gives you priority over cyclists',
          nl: 'Niets — uw groen geeft u voorrang op fietsers',
          ar: 'لا شيء — ضوءك الأخضر يمنحك الأولوية على الدراجين',
        },
      },
      {
        id: 'q-tls2-9o2',
        text: {
          en: 'Check mirrors and blind spot for cyclists crossing your path',
          nl: 'Spiegels en dode hoek controleren op fietsers die uw pad kruisen',
          ar: 'تحقق من المرايا والنقطة العمياء بحثا عن دراجين يعبرون مسارك',
        },
      },
      {
        id: 'q-tls2-9o3',
        text: {
          en: 'Wait for the cycle signal to turn red before turning',
          nl: 'Wachten tot het fietssignaal rood is voor het afslaan',
          ar: 'انتظر حتى تتحول إشارة الدراجات إلى الأحمر قبل الانعطاف',
        },
      },
    ],
    correctAnswerId: 'q-tls2-9o2',
    explanation: {
      en: 'A green cycle signal means cyclists are actively crossing. Always check mirrors and blind spot before turning right. Your green does not remove the duty to check.',
      nl: 'Een groen fietssignaal betekent dat fietsers oversteken. Controleer altijd spiegels en dode hoek voor rechtsaf slaan. Uw groen heft de kijkplicht niet op.',
      ar: 'إشارة الدراجات الخضراء تعني أن الدراجين يعبرون. تحقق دائما من المرايا والنقطة العمياء قبل الانعطاف يمينا. ضوءك الأخضر لا يلغي واجب التحقق.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-10',
    animationId: null,
    text: {
      en: 'You have stopped at a red light. An emergency vehicle with flashing blue lights and siren is behind you. What should you do?',
      nl: 'U staat stil bij rood licht. Achter u komt een hulpdienst met zwaailicht en sirene. Wat moet u doen?',
      ar: 'توقفت عند الضوء الأحمر. مركبة طوارئ خلفك بأضواء زرقاء ومستعرة وصفارة إنذار. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-10o1',
        text: {
          en: 'Stay where you are; do not cross the red light',
          nl: 'Blijf staan; rijd niet door rood',
          ar: 'ابق مكانك؛ لا تعبر الضوء الأحمر',
        },
      },
      {
        id: 'q-tls2-10o2',
        text: {
          en: 'If safe and necessary, move forward (e.g. into the junction) to let it pass, then stop',
          nl: 'Indien veilig en nodig, vooruit rijden (bijv. de kruising op) om ruimte te maken, daarna stoppen',
          ar: 'إن كان ذلك آمنا ولزاما، تقدم (مثلا إلى التقاطع) ليمر، ثم توقف',
        },
      },
      {
        id: 'q-tls2-10o3',
        text: {
          en: 'Reverse to make room',
          nl: 'Achteruit rijden om ruimte te maken',
          ar: 'ارجع للخلف لإفساح المجال',
        },
      },
    ],
    correctAnswerId: 'q-tls2-10o2',
    explanation: {
      en: 'You must give way to emergency vehicles. If you are at a red light, you may move forward carefully to let them pass if it is safe and you do not endanger others.',
      nl: 'U moet voorrang verlenen aan hulpdiensten. Bij rood licht mag u voorzichtig vooruitrijden om ze door te laten als het veilig is en u anderen niet in gevaar brengt.',
      ar: 'يجب أن تمنح الأولوية لمركبات الطوارئ. عند الضوء الأحمر، يجوز لك التقدم بحذر لإفساح المجال إذا كان ذلك آمنا ودون تعريض الآخرين للخطر.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-11',
    animationId: null,
    text: {
      en: 'At a pedestrian crossing with lights, the pedestrian light has just turned red. A pedestrian is still crossing slowly. Your light turns green. May you go?',
      nl: 'Bij een voetgangerslicht is het voetgangerslicht net rood geworden. Een voetganger steekt nog langzaam over. Uw licht wordt groen. Mag u rijden?',
      ar: 'عند معبر مشاة مع إشارات، تحول ضوء المشاة للتو إلى أحمر. أحد المشاة لا يزال يعبر ببطء. يتحول ضوءك إلى أخضر. هل يجوز لك المضي؟',
    },
    options: [
      {
        id: 'q-tls2-11o1',
        text: {
          en: 'Yes; the pedestrian had their turn',
          nl: 'Ja; de voetganger was aan de beurt',
          ar: 'نعم؛ كان دور المشاة',
        },
      },
      {
        id: 'q-tls2-11o2',
        text: {
          en: 'Only when the crossing is clear; you must not endanger anyone still on it',
          nl: 'Als de oversteek vrij is; u mag niemand op de oversteek in gevaar brengen',
          ar: 'فقط عندما يكون المعبر خاليا؛ لا تعرض من لا يزال عليه للخطر',
        },
      },
      {
        id: 'q-tls2-11o3',
        text: {
          en: 'Sound your horn so they hurry',
          nl: 'Claxonneren zodat ze opschieten',
          ar: 'استخدم البوق ليسرعوا',
        },
      },
    ],
    correctAnswerId: 'q-tls2-11o2',
    explanation: {
      en: 'You may proceed when your light is green only when you can do so without endangering or obstructing pedestrians who are still on the crossing.',
      nl: 'U mag pas doorrijden als uw licht groen is wanneer u dat kunt zonder voetgangers die nog op de oversteek zijn te hinderen of in gevaar te brengen.',
      ar: 'يجوز لك المضي عندما يكون ضوءك أخضر فقط عندما تستطيع ذلك دون تعريض المشاة الذين لا يزالون على المعبر للخطر أو عرقلة سيرهم.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls2-12',
    animationId: null,
    text: {
      en: 'You approach a junction at night. All traffic lights are off. A yellow light flashes on a central bollard. Traffic is approaching from multiple directions. What do you do?',
      nl: 'U nadert een kruising \'s nachts. Alle verkeerslichten zijn uit. Een geel licht knippert op een middenpaaltje. Verkeer nadert van meerdere kanten. Wat doet u?',
      ar: 'تقترب من تقاطع ليلا. جميع إشارات المرور مطفأة. ضوء أصفر يومض على عمود وسطي. مركبات تقترب من عدة اتجاهات. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls2-12o1',
        text: {
          en: 'Stop completely until the yellow light stops flashing',
          nl: 'Volledig stoppen totdat het gele licht stopt met knipperen',
          ar: 'توقف تماما حتى يتوقف الضوء الأصفر عن الوميض',
        },
      },
      {
        id: 'q-tls2-12o2',
        text: {
          en: 'Proceed with extra caution, applying normal right-of-way rules',
          nl: 'Met extra voorzichtigheid verder rijden, normale voorrangsregels toepassen',
          ar: 'تابع بحذر شديد، مطبقا قواعد الأولوية العادية',
        },
      },
      {
        id: 'q-tls2-12o3',
        text: {
          en: 'Take priority — the yellow means the junction is clear',
          nl: 'Voorrang nemen — het gele licht betekent dat de kruising vrij is',
          ar: 'خذ الأولوية — الأصفر يعني أن التقاطع خال',
        },
      },
    ],
    correctAnswerId: 'q-tls2-12o2',
    explanation: {
      en: 'A flashing yellow light warns of hazard or special conditions — not a clear junction. Proceed carefully and apply normal priority rules; give way to the right on equal roads.',
      nl: 'Een knipperend geel licht waarschuwt voor gevaar of bijzondere omstandigheden — geen vrije kruising. Rijd voorzichtig en pas normale voorrangsregels toe; rechts heeft voorrang.',
      ar: 'الضوء الأصفر الوميض يحذر من خطر أو ظروف خاصة — وليس تقاطعا خاليا. تابع بحذر وطبق قواعد الأولوية العادية؛ الأولوية لليمين على الطرق المتكافئة.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-new-3',
    animationId: null,
    text: {
      en: 'You are on the motorway. The overhead sign above your lane shows a red X. The sign above the lane to your left shows a white arrow pointing left. What do you do?',
      nl: 'U rijdt op de snelweg. Het matrixbord boven uw rijstrook toont een rode X. Het bord boven de rijstrook links van u toont een witte pijl naar links. Wat doet u?',
      ar: 'أنت على الطريق السريع. اللوحة فوق مسارك تظهر علامة X حمراء. اللوحة فوق المسار على يسارك تظهر سهما أبيض يشير إلى اليسار. ماذا تفعل؟',
    },
    options: [
      {
        id: 'q-tls-new-3-o1',
        text: {
          en: 'Continue in your lane — overhead signs on motorways are advisory only',
          nl: 'Doorrijden op uw rijstrook — matrixborden zijn alleen adviserend',
          ar: 'تابع في مسارك — اللوحات العلوية للطريق السريع استشارية فقط',
        },
      },
      {
        id: 'q-tls-new-3-o2',
        text: {
          en: 'Signal and move safely to the left lane — your lane is closed',
          nl: 'Richting aangeven en veilig naar de linkerrijstrook gaan — uw rijstrook is afgesloten',
          ar: 'أشر وانتقل بأمان إلى المسار الأيسر — مسارك مغلق',
        },
      },
      {
        id: 'q-tls-new-3-o3',
        text: {
          en: 'Stop immediately in your lane and switch on hazard lights',
          nl: 'Onmiddellijk op uw rijstrook stoppen en alarmlichten aanzetten',
          ar: 'توقف فورا في مسارك وشغل أضواء الطوارئ',
        },
      },
    ],
    correctAnswerId: 'q-tls-new-3-o2',
    explanation: {
      en: 'A red X means your lane is closed — you must leave it. A white arrow shows the permitted direction. Overhead motorway signs are legally binding, not advisory.',
      nl: 'Een rode X betekent dat uw rijstrook is afgesloten — u moet die verlaten. Een witte pijl toont de toegestane richting. Matrixborden zijn wettelijk verplicht, niet adviserend.',
      ar: 'علامة X الحمراء تعني أن مسارك مغلق — يجب مغادرته. السهم الأبيض يظهر الاتجاه المسموح. اللوحات العلوية للطريق السريع ملزمة قانونا وليست استشارية.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-new-4',
    animationId: 'tls-anim-5',
    text: {
      en: 'You are driving within a built-up area. A bus at a marked bus stop ahead switches on its right indicator. It has not yet moved. What should you do?',
      nl: 'U rijdt binnen de bebouwde kom. Een bus bij een bushalte voor u zet zijn rechterrichtingaanwijzer aan. Hij is nog niet weggereden. Wat moet u doen?',
      ar: 'أنت تسير داخل منطقة مبنية. حافلة عند محطة حافلات أمامك تشغل مؤشر الانعطاف الأيمن. لم تتحرك بعد. ماذا يجب أن تفعل؟',
    },
    options: [
      {
        id: 'q-tls-new-4-o1',
        text: {
          en: 'Maintain speed — the bus has not moved yet so you still have priority',
          nl: 'Snelheid handhaven — de bus is nog niet weggereden dus u heeft nog voorrang',
          ar: 'حافظ على السرعة — الحافلة لم تتحرك بعد فلا تزال الأولوية لك',
        },
      },
      {
        id: 'q-tls-new-4-o2',
        text: {
          en: 'Reduce speed and be prepared to give way — the bus has priority when leaving the stop',
          nl: 'Snelheid verminderen en klaarstaan om voor te laten — de bus heeft voorrang bij het verlaten van de halte',
          ar: 'قلل السرعة وكن مستعدا لإعطاء الأولوية — الحافلة لها الأولوية عند مغادرة المحطة',
        },
      },
      {
        id: 'q-tls-new-4-o3',
        text: {
          en: 'Flash your headlights to signal to the bus driver that you are approaching',
          nl: 'Lichten knipperen om de buschauffeur te sein dat u nadert',
          ar: 'وميض الأضواء لإشارة السائق إلى أنك تقترب',
        },
      },
    ],
    correctAnswerId: 'q-tls-new-4-o2',
    explanation: {
      en: 'Under RVV 1990 article 50, within a built-up area you must give way to buses leaving a marked bus stop. As soon as the bus signals its intention to pull out, reduce speed and be prepared to stop.',
      nl: 'Artikel 50 RVV verplicht u binnen de bebouwde kom voorrang te geven aan bussen die een bushalte verlaten. Zodra de bus aangeeft te willen invoegen, vertraag dan en wees bereid te stoppen.',
      ar: 'بموجب المادة 50 من لائحة المرور RVV 1990، يجب إعطاء الأولوية للحافلات التي تغادر محطة محددة داخل المناطق المبنية. بمجرد إشارة الحافلة لنيتها الانطلاق، قلل السرعة وكن مستعدا للتوقف.',
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-11',
    text: {
      en: 'What must you do when an emergency vehicle with blue flashing lights and siren is approaching?',
      nl: 'Wat moet u doen als een hulpvoertuig met blauwe zwaailichten en sirene nadert?',
      ar: 'ماذا يجب أن تفعل عندما تقترب مركبة طوارئ مع أضواء زرقاء ووميض وصفارة؟'
    },
    options: [
      {
        id: 'q-tls-11o1',
        text: { en: 'Maintain your speed and stay in your lane', nl: 'Uw snelheid aanhouden en in uw rijstrook blijven', ar: 'حافظ على سرعتك وابق في حارتك' }
      },
      {
        id: 'q-tls-11o2',
        text: { en: 'Give way immediately by moving aside where safe', nl: 'Onmiddellijk voorrang geven door veilig opzij te gaan', ar: 'فسح الطريق فوراً بالتحرك جانباً حيث يكون ذلك آمناً' }
      },
      {
        id: 'q-tls-11o3',
        text: { en: 'Sound your horn to alert other drivers', nl: 'Claxonneren om andere bestuurders te waarschuwen', ar: 'استخدم البوق لتنبيه السائقين الآخرين' }
      }
    ],
    correctAnswerId: 'q-tls-11o2',
    explanation: {
      en: 'Emergency vehicles (police, ambulance, fire) with blue flashing lights have absolute priority (RVV 1990 art. 91). You must give way immediately by moving aside where safe, without endangering others.',
      nl: 'Hulpvoertuigen (politie, ambulance, brandweer) met blauwe zwaailichten hebben absolute voorrang (RVV 1990 art. 91). U moet onmiddellijk voorrang geven door veilig opzij te gaan, zonder anderen in gevaar te brengen.',
      ar: 'تتمتع مركبات الطوارئ (شرطة، إسعاف، مطافئ) التي تعمل بأضواء زرقاء ووميض بالأولوية المطلقة (المادة 91 من RVV 1990). يجب عليك فسح الطريق فوراً بالتحرك جانباً حيث يكون ذلك آمناً دون تعريض الآخرين للخطر.'
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-12',
    text: {
      en: 'What do red tail lights on a vehicle ahead indicate?',
      nl: 'Wat geven rode achterlichten op een voertuig voor u aan?',
      ar: 'ماذا تشير الأضواء الخلفية الحمراء على مركبة أمامك؟'
    },
    options: [
      { id: 'q-tls-12o1', text: { en: 'The vehicle is turning right', nl: 'Het voertuig slaat rechtsaf', ar: 'المركبة تنعطف يميناً' } },
      { id: 'q-tls-12o2', text: { en: 'The vehicle is reversing or has brake lights illuminated', nl: 'Het voertuig rijdt achteruit of de remlichten branden', ar: 'المركبة تعكس للخلف أو أضواء الفرامل تعمل' } },
      { id: 'q-tls-12o3', text: { en: 'The vehicle is a taxi', nl: 'Het voertuig is een taxi', ar: 'المركبة سيارة أجرة' } }
    ],
    correctAnswerId: 'q-tls-12o2',
    explanation: {
      en: 'Red tail lights indicate the vehicle\'s brake lights are on (braking) or it is reversing. Always increase your distance when you see brake lights to avoid collision.',
      nl: 'Rode achterlichten betekenen dat het remlicht brandt (remmen) of dat achteruit wordt gereden. Verhoog altijd de volgafstand als u remlichten ziet om een aanrijding te voorkomen.',
      ar: 'تشير الأضواء الخلفية الحمراء إلى أن أضواء الفرامل تعمل (أي أنه يفرمل) أو أنه يتراجع للخلف. زِد دائماً مسافة الأمان عندما ترى أضواء الفرامل لتجنب الاصطدام.'
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-13',
    text: {
      en: 'What does a white reversing light indicate?',
      nl: 'Wat geeft een wit achteruitrijlicht aan?',
      ar: 'ماذا تشير لمبة الرجوع للخلف البيضاء؟'
    },
    options: [
      { id: 'q-tls-13o1', text: { en: 'The vehicle is malfunctioning', nl: 'Het voertuig heeft een defect', ar: 'المركبة بها عطل' } },
      { id: 'q-tls-13o2', text: { en: 'The vehicle is reversing or about to reverse', nl: 'Het voertuig rijdt achteruit of gaat achteruit', ar: 'المركبة تتراجع للخلف أو على وشك التراجع' } },
      { id: 'q-tls-13o3', text: { en: 'The vehicle is being towed', nl: 'Het voertuig wordt gesleept', ar: 'المركبة يتم سحبها' } }
    ],
    correctAnswerId: 'q-tls-13o2',
    explanation: {
      en: 'White reversing lights illuminate when a vehicle is reversing or about to reverse. Be alert to vehicles backing up, especially in car parks and residential areas.',
      nl: 'Een wit achteruitrijlicht brandt als een voertuig achteruit rijdt of dat gaat doen. Let op achteruitrijdende voertuigen, vooral op parkeerterreinen en in woonwijken.',
      ar: 'تضيء لمبة الرجوع للخلف البيضاء عندما تكون المركبة تتراجع للخلف أو على وشك التراجع. انتبه للمركبات التي تتراجع، خصوصاً في مواقف السيارات والمناطق السكنية.'
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-14',
    text: {
      en: 'What does a flashing amber light on a traffic signal mean?',
      nl: 'Wat betekent een knipperend oranje licht op een verkeerslicht?',
      ar: 'ماذا تعني إشارة الكهرماني الوميض على إشارة المرور؟'
    },
    options: [
      { id: 'q-tls-14o1', text: { en: 'The signal is broken; you may proceed at your discretion', nl: 'Het licht is defect; u mag verder rijden naar eigen inzicht', ar: 'الإشارة معطلة؛ يمكنك متابعة السير حسب تقديرك' } },
      { id: 'q-tls-14o2', text: { en: 'You must proceed cautiously as priority to the right applies', nl: 'U moet voorzichtig verder rijden; voorrang van rechts geldt', ar: 'يجب أن تتقدم بحذر لأن أولوية اليمين تنطبق' } },
      { id: 'q-tls-14o3', text: { en: 'Stop and wait for a green light', nl: 'Stoppen en wachten op groen', ar: 'توقف وانتظر ضوءاً أخضر' } }
    ],
    correctAnswerId: 'q-tls-14o2',
    explanation: {
      en: 'A flashing amber light means the traffic signal is not fully operational. Treat the intersection like an unmarked junction where priority to the right applies. Proceed cautiously and be prepared to give way.',
      nl: 'Knipperend oranje betekent dat het verkeerslicht niet volledig functioneert. Behandel de kruising als onbewaakt, met voorrang van rechts. Rijd voorzichtig en wees bereid voorrang te verlenen.',
      ar: 'يعني وميض الكهرماني أن إشارة المرور ليست تعمل بشكل كامل. اعتبر التقاطع مثل تقاطع بدون إشارات حيث تنطبق أولوية اليمين. تقدم بحذر واستعد لتفسح الطريق.'
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-15',
    text: {
      en: 'What does a red X symbol on an overhead motorway signal indicate?',
      nl: 'Wat geeft een rood kruis op een boven de snelweg hangend matrixbord aan?',
      ar: 'ماذا تشير علامة X الحمراء على إشارة الطريق السريع المعلقة؟'
    },
    options: [
      { id: 'q-tls-15o1', text: { en: 'That lane is closed and must not be used', nl: 'Die rijstrook is afgesloten en mag niet worden gebruikt', ar: 'تلك الحارة مغلقة ولا يجوز استخدامها' } },
      { id: 'q-tls-15o2', text: { en: 'Speed limit for that lane only', nl: 'Alleen snelheidslimiet voor die rijstrook', ar: 'حد السرعة لتلك الحارة فقط' } },
      { id: 'q-tls-15o3', text: { en: 'Parking is forbidden in that lane', nl: 'Parkeren is in die rijstrook verboden', ar: 'الوقوف محظور في تلك الحارة' } }
    ],
    correctAnswerId: 'q-tls-15o1',
    explanation: {
      en: 'A red X symbol on an overhead motorway signal indicates that lane is closed. Do not enter that lane or you risk collision with other vehicles or stationary obstacles.',
      nl: 'Een rood kruis boven de snelweg betekent dat die rijstrook is afgesloten. Rijd die strook niet in, anders loopt u risico op een aanrijding of een stilstaand obstakel.',
      ar: 'تشير علامة X الحمراء على إشارة الطريق السريع المعلقة إلى أن تلك الحارة مغلقة. لا تدخل إليها لأنك قد تتعرض لاصطدام مع مركبات أخرى أو عوائق ثابتة.'
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-16',
    text: {
      en: 'What does a green arrow with a horizontal line through it on a traffic signal indicate?',
      nl: 'Wat geeft een groene pijl met een horizontale streep op een verkeerslicht aan?',
      ar: 'ماذا تشير سهم أخضر عليه خط أفقي في إشارة المرور؟'
    },
    options: [
      { id: 'q-tls-16o1', text: { en: 'The turn is compulsory', nl: 'Afslaan is verplicht', ar: 'الانعطاف إلزامي' } },
      { id: 'q-tls-16o2', text: { en: 'The turn is no longer compulsory; general traffic light rules now apply', nl: 'Afslaan is niet langer verplicht; algemene lichtregels gelden nu', ar: 'لم يعد الانعطاف إلزامياً؛ تُطبق الآن قواعد إشارة المرور العامة' } },
      { id: 'q-tls-16o3', text: { en: 'Turning is prohibited', nl: 'Afslaan is verboden', ar: 'الانعطاف محظور' } }
    ],
    correctAnswerId: 'q-tls-16o2',
    explanation: {
      en: 'A green arrow with a line through it (or arrow turning off) indicates the protected turn phase is ending. General traffic light rules now apply; you must give way to opposing traffic if turning.',
      nl: 'Een groene pijl met streep (of een uitgaande pijl) betekent dat de beschermde afslaande fase eindigt. De gewone lichtregels gelden nu; u moet bij afslaan tegenliggers voor laten gaan.',
      ar: 'يشير السهم الأخضر عليه خط (أو إطفاء السهم) إلى أن مرحلة الانعطاف المحمية تنتهي. تُطبق الآن قواعد المرور العامة لإشارات المرور؛ يجب أن تفسح الطريق للمركبات القادمة من الاتجاه المقابل إذا كنت ستنعطف.'
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-17',
    text: {
      en: 'You are at a traffic light and the green arrow allows you to turn. There is a cyclist on the cycle path crossing your path. What must you do?',
      nl: 'U staat voor een verkeerslicht; de groene pijl staat afslaan toe. Er rijdt een fietser op het fietspad uw weg kruisend. Wat moet u doen?',
      ar: 'أنت عند إشارة مرور والسهم الأخضر يسمح لك بالانعطاف. يوجد راكب دراجة في مسار الدراجات يعبر طريقك. ماذا يجب أن تفعل؟'
    },
    options: [
      { id: 'q-tls-17o1', text: { en: 'Proceed — the green arrow protects you completely', nl: 'Doorrijden — de groene pijl beschermt u volledig', ar: 'تابع — السهم الأخضر يحميك بالكامل' } },
      { id: 'q-tls-17o2', text: { en: 'Give way to the cyclist even with a green arrow', nl: 'Voorrang verlenen aan de fietser, ook met groene pijl', ar: 'فسح الطريق لراكب الدراجة حتى مع السهم الأخضر' } },
      { id: 'q-tls-17o3', text: { en: 'Sound your horn and proceed', nl: 'Claxonneren en doorrijden', ar: 'استخدم البوق وتابع' } }
    ],
    correctAnswerId: 'q-tls-17o2',
    explanation: {
      en: 'Even with a green arrow, you must give way to cyclists on a cycle path (fietspad) crossing your turning path. A green arrow protects you from opposing traffic, not from vulnerable road users.',
      nl: 'Ook met een groene pijl moet u voorrang verlenen aan fietsers op een fietspad dat uw afslag kruist. Een groene pijl beschermt u tegen tegenliggers, niet tegen kwetsbare weggebruikers.',
      ar: 'حتى مع السهم الأخضر، يجب عليك فسح الطريق لراكبي الدراجات في مسار الدراجات (fietspad) الذي يعبر طريق انعطافك. السهم الأخضر يحميك من المركبات القادمة من الاتجاه المقابل، وليس من مستخدمي الطريق الأكثر عرضة للخطر.'
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-18',
    text: {
      en: 'What does amber flashing lights on a roadside or barrier indicate?',
      nl: 'Wat geven knipperende oranje lampjes langs de weg of op een afzetting aan?',
      ar: 'ماذا تشير الأضواء الكهرمانية الوميض على جانب الطريق أو الحاجز؟'
    },
    options: [
      { id: 'q-tls-18o1', text: { en: 'Danger ahead; reduce speed immediately', nl: 'Gevaar vooruit; onmiddellijk vertragen', ar: 'خطر أمامك؛ خفّض السرعة فوراً' } },
      { id: 'q-tls-18o2', text: { en: 'A temporary hazard or roadworks; proceed with caution', nl: 'Tijdelijk gevaar of wegwerkzaamheden; voorzichtig verder rijden', ar: 'خطر مؤقت أو أعمال طريق؛ تقدم بحذر' } },
      { id: 'q-tls-18o3', text: { en: 'You must stop before proceeding', nl: 'U moet stoppen voordat u verder rijdt', ar: 'يجب أن تتوقف قبل المتابعة' } }
    ],
    correctAnswerId: 'q-tls-18o2',
    explanation: {
      en: 'Amber flashing lights on roadside equipment or barriers warn of temporary hazards, roadworks, or emergency situations. Reduce speed and proceed with extra caution, being prepared to stop.',
      nl: 'Knipperend oranje op apparatuur of afzettingen langs de weg waarschuwt voor tijdelijk gevaar, werkzaamheden of noodsituaties. Vertraag en rijd extra voorzichtig; wees bereid te stoppen.',
      ar: 'تحذر الأضواء الكهرمانية الوميض على معدات جانب الطريق أو الحواجز من مخاطر مؤقتة أو أعمال طريق أو حالات طوارئ. خفّض السرعة وتقدم بحذر شديد، وكن مستعداً للتوقف.'
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-19',
    text: {
      en: 'At a traffic light intersection, you have a green light but see pedestrians still crossing. What must you do?',
      nl: 'Op een kruising met verkeerslicht heeft u groen, maar u ziet nog voetgangers oversteken. Wat moet u doen?',
      ar: 'عند تقاطع مع إشارة مرور لديك ضوء أخضر لكن ترى مشاة ما زالوا يعبرون. ماذا يجب أن تفعل؟'
    },
    options: [
      { id: 'q-tls-19o1', text: { en: 'Proceed — you have right of way with a green light', nl: 'Doorrijden — u heeft voorrang bij groen', ar: 'تابع — لديك حق المرور مع الضوء الأخضر' } },
      { id: 'q-tls-19o2', text: { en: 'Stop and give way to the pedestrians', nl: 'Stoppen en voorrang verlenen aan de voetgangers', ar: 'توقف وفسح الطريق للمشاة' } },
      { id: 'q-tls-19o3', text: { en: 'Sound your horn to alert pedestrians', nl: 'Claxonneren om voetgangers te waarschuwen', ar: 'استخدم البوق لتنبيه المشاة' } }
    ],
    correctAnswerId: 'q-tls-19o2',
    explanation: {
      en: 'You must always give way to pedestrians legally crossing, even if your light is green. Pedestrians who started crossing on a "walk" signal have right of way to complete their crossing safely.',
      nl: 'U moet altijd voorrang verlenen aan voetgangers die legaal oversteken, ook als uw licht groen is. Voetgangers die bij een groen voetgangerslicht zijn begonnen, mogen hun oversteek veilig voltooien.',
      ar: 'يجب أن تفسح الطريق للمشاة الذين يعبرون بشكل قانوني دائماً، حتى لو كان ضوءك أخضر. المشاة الذين بدأوا العبور عند إشارة "المشي" لهم حق المرور لإكمال عبورهم بأمان.'
    },
    subject: 'Traffic Lights & Signals',
  },
  {
    id: 'q-tls-20',
    text: {
      en: 'What is the purpose of traffic light signals in traffic control?',
      nl: 'Wat is het doel van verkeerslichten in het verkeersmanagement?',
      ar: 'ما الهدف من إشارات المرور في تنظيم حركة السير؟'
    },
    options: [
      { id: 'q-tls-20o1', text: { en: 'To allow drivers to proceed freely without concern for other traffic', nl: 'Bestuurders vrij laten rijden zonder rekening te houden met ander verkeer', ar: 'للسماح للسائقين بالمرور بحرية دون القلق من حركة المرور الأخرى' } },
      { id: 'q-tls-20o2', text: { en: 'To regulate traffic flow, assign right-of-way, and manage safe movement at intersections', nl: 'Het verkeer regelen, voorrang vastleggen en veilig gedrag op kruisingen coördineren', ar: 'لتنظيم تدفق المرور وتحديد حق المرور وإدارة الحركة الآمنة عند التقاطعات' } },
      { id: 'q-tls-20o3', text: { en: 'To penalize drivers who break the rules', nl: 'Bestuurders straffen die de regels overtreden', ar: 'لمعاقبة السائقين الذين يخالفون القواعد' } }
    ],
    correctAnswerId: 'q-tls-20o2',
    explanation: {
      en: 'Traffic lights regulate traffic flow by controlling when vehicles and pedestrians move, preventing conflicts and managing safe intersections. All road users must obey traffic signals (RVV 1990).',
      nl: 'Verkeerslichten regelen het verkeer door te bepalen wanneer voertuigen en voetgangers mogen rijden of oversteken, conflicten te voorkomen en kruisingen veilig te laten verlopen. Alle weggebruikers moeten lichtsignalen opvolgen (RVV 1990).',
      ar: 'تنظم إشارات المرور تدفق حركة المرور عبر التحكم بوقت تحرك المركبات والمشاة، ما يمنع التعارضات ويضمن إدارة تقاطعات آمنة. يجب على جميع مستخدمي الطريق اتباع إشارات المرور (RVV 1990).'
    },
    subject: 'Traffic Lights & Signals',
  },
];
